import { Stack, Text } from '@chakra-ui/react';

import { InView } from 'react-intersection-observer';
import LearnerDashboardSubscriptionListItem from '@/client/components/learners/dashboard/components/subscriptions/LearnerDashboardSubscriptionListItem';
import Loading from '@/client/components/media/Loading';
import { OrderingDirection } from '@/client/services/api/graphql/gql/graphql';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import { PaginationState } from '@tanstack/react-table';
import { useGetBaseProduct } from '@/client/services/hooks/content/products/useGetProduct';
import { useListProductsInfinite } from '@/client/services/hooks/content/products/useListProducts';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function LearnerDashboardSubscriptionsBrowseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [{ pageIndex, pageSize }] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data: baseProduct } = useGetBaseProduct();

  const getAlternativeProducts = () => {
    if (!baseProduct?.allowRelatedProducts) {
      return [];
    }
    if (baseProduct.relatedProducts.length === 0) {
      return null;
    }
    return baseProduct.relatedProducts;
  };

  const { data, isLoading, isError, isFetchingNextPage, fetchNextPage } = useListProductsInfinite({
    page: pageIndex,
    pageSize,
    includeTotalCount: true,
    archived: { eq: false },
    id: { in: getAlternativeProducts() },
    ordering: [{ field: 'createdAt', direction: OrderingDirection.Desc }],
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error</div>;
  }

  const { pages } = data;

  return (
    <Stack spacing={4}>
      <Text fontSize="20px" fontWeight={600} color="baseBlack">
        {t('learners.dashboard.subscriptions.suggested')}
      </Text>

      {pages.length === 0 ? (
        <Text fontSize="16px" fontWeight={500} color="neutral.600">
          {t('learners.dashboard.subscriptions.noSuggestedSubscriptions')}
        </Text>
      ) : (
        <Stack>
          {pages.map((page) =>
            page.products.map((product) => (
              <LearnerDashboardSubscriptionListItem
                key={product.id}
                title={product.title}
                description={product.description}
                price={product.price}
                subscriptionDuration={product.subscriptionDuration}
                image={product.images[0] || '/public/images/cardPlaceholder.png'}
                actionGroup={
                  <OutlineButton
                    size="sm"
                    onClick={() => navigate(`/product/${product.id}/payment-method`)}
                  >
                    {t('learners.dashboard.subscriptions.viewDetails')}
                  </OutlineButton>
                }
              />
            )),
          )}
          <InView
            rootMargin="-30px"
            as="div"
            threshold={0}
            onChange={(inView) => {
              if (inView) {
                fetchNextPage();
              }
            }}
          />
          {isFetchingNextPage && <Loading />}
        </Stack>
      )}
    </Stack>
  );
}
